import { useLayoutEffect, useState } from "react";
import { Alert, Stack, useTheme } from "@mui/material";
import { Button, FoodsTextInput, Typography, WhiteBox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import {
    TEMPLATE_NAME_MAX_LENGTH,
    type TemplateDetail,
} from "src/api/client-api/actionPlanTemplates/useActionPlanTemplate";
import { useTemplateSsqInformation } from "src/api/client-api/actionPlanTemplates/useTemplateActionOptions";
import type { useTemplateSectorPages } from "src/api/client-api/actionPlanTemplates/useTemplateCreationOptions";
import type { DelayedSearch } from "src/utils/useDelayedSearch";
import { FoodsServerSelectField } from "src/components/EntityForm/FoodsFields";
import { formatVersion } from "src/utils/formatting";
import { TemplateStatusBadge } from "./TemplateStatusBadge";

type Props = {
    template: TemplateDetail;
    name: string;
    selectedSector: { id: number; name: string } | null;
    busy: boolean;
    errors: Record<string, string>;
    sectors: ReturnType<typeof useTemplateSectorPages>;
    sectorSearch: DelayedSearch;
    sectorDetached: boolean;
    onNameChange: (name: string) => void;
    onSectorChange: (sector: { id: number; name: string } | null) => void;
};

export const TemplateGeneralInformationForm = (props: Props) => {
    const {
        template,
        name,
        selectedSector,
        busy,
        errors,
        sectors,
        sectorSearch,
        sectorDetached,
        onNameChange,
        onSectorChange,
    } = props;

    const { t } = useTranslation();
    const theme = useTheme();
    const ssq = useTemplateSsqInformation(template.ssqId);
    const [selectContainer, setSelectContainer] = useState<HTMLDivElement | null>(null);
    const [selectWidth, setSelectWidth] = useState<number>();
    useLayoutEffect(() => {
        if (selectContainer === null) return;

        const updateWidth = () => {
            const width = selectContainer.getBoundingClientRect().width;
            setSelectWidth(width > 0 ? width : undefined);
        };
        updateWidth();

        if (typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(updateWidth);
        observer.observe(selectContainer);
        return () => observer.disconnect();
    }, [selectContainer]);
    const searchPending = sectorSearch.search !== sectorSearch.delayedSearch;
    const sectorOptions = searchPending ? [] : (sectors.data ?? []);
    const noResult =
        sectors.isLoading || searchPending ?
            t("settings.action_plan_templates.form.loading_options", "Loading options…")
        : sectorSearch.search.trim() ? t("settings.action_plan_templates.form.no_options", "No options available")
        : t("settings.action_plan_templates.form.no_sectors_for_ssq", "No industry sectors are attached to this SSQ.");
    const selectedVersion = ssq.data?.versions?.find((version) => version.id === template.ssqVersionId);
    const versionLabel =
        selectedVersion ? formatVersion(selectedVersion)
        : ssq.isLoading ? t("settings.action_plan_templates.form.loading_options", "Loading options…")
        : ssq.isError ? t("settings.action_plan_templates.ssq_unavailable", "SSQ unavailable")
        : t(
                "settings.action_plan_templates.publish.ssq_version_unavailable",
                "The selected SSQ version is no longer available. Create a new template with another version.",
            );

    return (
        <>
            <Typography variant="h3">
                {t("settings.action_plan_templates.detail.general_information", "General information")}
            </Typography>
            <WhiteBox sx={{ p: theme.spacing(3) }}>
                <Stack gap={theme.spacing(3)}>
                    <Typography component="label" htmlFor="edit-template-name" variant="body-bold">
                        {t("settings.action_plan_templates.form.name", "Template name")}*
                    </Typography>
                    <FoodsTextInput
                        value={name}
                        onChange={onNameChange}
                        disabled={busy}
                        error={Boolean(errors.name)}
                        ExtraInputProps={{
                            id: "edit-template-name",
                            required: true,
                            inputProps: {
                                maxLength: TEMPLATE_NAME_MAX_LENGTH,
                                "aria-describedby": errors.name ? "edit-name-error" : undefined,
                            },
                        }}
                    />
                    {errors.name && (
                        <Typography id="edit-name-error" role="alert">
                            {errors.name}
                        </Typography>
                    )}
                    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={theme.spacing(2)}>
                        <Typography variant="body-bold">
                            {t("settings.action_plan_templates.column.status", "Status")}:
                        </Typography>
                        <TemplateStatusBadge status={template.status} />
                        <Typography variant="body-bold">
                            {t("settings.action_plan_templates.column.ssq", "SSQ")}:
                        </Typography>
                        <Typography>
                            {ssq.data?.title ??
                                template.ssqTitle ??
                                (ssq.isLoading ? "…" : (
                                    t("settings.action_plan_templates.ssq_unavailable", "SSQ unavailable")
                                ))}
                        </Typography>
                        <Typography variant="body-bold">
                            {t("settings.action_plan_templates.form.ssq_version", "SSQ version")}:
                        </Typography>
                        <Typography>{versionLabel}</Typography>
                    </Stack>
                    <Stack ref={setSelectContainer} gap={theme.spacing(1)}>
                        <FoodsServerSelectField<{ id: number; name: string }>
                            appearance="product-v2"
                            label={t("settings.action_plan_templates.column.sector", "Industry sector")}
                            required
                            errorText={errors.sectorId}
                            options={sectorOptions}
                            selectedOption={selectedSector}
                            onChange={(value) => {
                                onSectorChange(value);
                                sectorSearch.resetSearch();
                            }}
                            getId={(item) => item.id}
                            getName={(item) => item.name}
                            placeholder={t(
                                "settings.action_plan_templates.form.sector_placeholder",
                                "Select an industry sector",
                            )}
                            searchPlaceholder={t(
                                "settings.action_plan_templates.form.sector_search",
                                "Search industry sectors",
                            )}
                            emptyOptionsText={noResult}
                            noResultText={noResult}
                            defaultValueText={t("component.select.defaultvalue", "Default value")}
                            search={sectorSearch.search}
                            setSearch={sectorSearch.setSearch}
                            isSearchFound={() => true}
                            disabled={busy}
                            boxOptionsProps={selectWidth ? { width: selectWidth } : undefined}
                            infiniteScroll={{
                                hasNextPage: sectors.hasNextPage ?? false,
                                isFetchingNextPage: sectors.isFetchingNextPage,
                                onLoadMore: () => void sectors.fetchNextPage(),
                                loadingText: t(
                                    "settings.action_plan_templates.form.loading_options",
                                    "Loading options…",
                                ),
                            }}
                        />
                    </Stack>
                    {sectorDetached && (
                        <Typography role="alert">
                            {t(
                                "settings.action_plan_templates.editor.sector_not_attached",
                                "This industry sector is no longer attached to the template's SSQ. Select another one before saving.",
                            )}
                        </Typography>
                    )}
                    {sectors.isError && (
                        <Alert severity="error">
                            <Button variant="text" onClick={() => sectors.refetch()}>
                                {t("settings.action_plan_templates.retry", "Try again")}
                            </Button>
                        </Alert>
                    )}
                </Stack>
            </WhiteBox>
        </>
    );
};
